import { safeSocialLinks, type SocialLinks } from "../../shared/profileSocialLinks.js";
import { getProfileAvatarUrl } from "./profileAvatarUrl";
import { contentRequest, type CommunityContentBlock } from "./communityContent";
import { COMMUNITY_PURPOSE_FILTERS, type CommunityPurpose } from "./communityParticipation";
/**
 * Community (TBoard) data from Supabase direct — no Django.
 * Requires RLS: 001 (categories), 002 (posts view + postfile).
 * @see docs/supabase_rls/
 */
import { supabase } from "./supabase";
import { fixSupabasePublicStorageUrl, normalizeSupabaseProjectUrl } from "./supabaseUrl";
import { extractForeducatorMediaKeysFromHtml, removeForeducatorMediaObjects } from "./supabaseStorageCleanup";

const SUPABASE_URL = normalizeSupabaseProjectUrl(
  import.meta.env.VITE_SUPABASE_URL || "https://jmgoqpqyrnoamfjngcmy.supabase.co"
);
const MEDIA_BUCKET = "foreducator-media";

/** Supabase Storage public URL for a stored file path (e.g. tboard/images/...) */
export function getSupabaseMediaUrl(path: string | null | undefined): string {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return fixSupabasePublicStorageUrl(path);
  }
  const base = `${SUPABASE_URL}/storage/v1/object/public/${MEDIA_BUCKET}`;
  const built = path.startsWith("/") ? base + path : base + "/" + path;
  return fixSupabasePublicStorageUrl(built);
}

export type CommunityCategoryFromSupabase = {
  id: number;
  name: string;
  subcategories: { id: number; name: string; category_id: number }[];
};

/**
 * Fetches community categories + subcategories from Supabase (same shape as API).
 * Returns [] if Supabase is not configured or RLS fails.
 */
export async function getCommunityCategoriesFromSupabase(): Promise<CommunityCategoryFromSupabase[]> {
  if (!supabase) return [];

  const [
    { data: categories, error: catError },
    { data: subcategories, error: subError },
  ] = await Promise.all([
    supabase
      .from("tboard_category")
      .select("id, name")
      .order("order", { ascending: true })
      .order("name", { ascending: true }),
    supabase
      .from("tboard_subcategory")
      .select("id, name, category_id")
      .order("order", { ascending: true })
      .order("name", { ascending: true }),
  ]);

  if (catError || subError || !categories?.length) return [];

  const subByCategory = (subcategories || []).reduce(
    (acc, s) => {
      const cid = s.category_id;
      if (!acc[cid]) acc[cid] = [];
      acc[cid].push({
        id: s.id,
        name: s.name,
        category_id: cid,
      });
      return acc;
    },
    {} as Record<number, { id: number; name: string; category_id: number }[]>
  );

  return categories.map((c) => ({
    id: c.id,
    name: c.name,
    subcategories: subByCategory[c.id] ?? [],
  }));
}

// ─── Posts (list + detail) from view community_post_list_view ─────────────────

export type CommunityPostListItemFromSupabase = {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  hits: number;
  top_fixed: boolean;
  tags: string | null;
  category_legacy: string | null;
  category_new_id: number | null;
  subcategory_id: number | null;
  author_id: number | null;
  author_username: string | null;
  author_nickname: string | null;
  author_avatar_path: string | null;
  author_social_links?: SocialLinks;
  category_name: string | null;
  subcategory_name: string | null;
  num_comments: number;
  num_likes: number;
  head_image: string | null;
  file_upload: string | null;
  vibe_coding_link: string | null;
};

/** One lookup per result set, including replies; never one request per author icon. */
async function addAuthorSocialLinks(rows: { author_id: number | null; author_social_links?: SocialLinks }[]): Promise<void> {
  const ids = [...new Set(rows.flatMap(row => row.author_id == null ? [] : [row.author_id]))];
  if (!supabase || !ids.length) return;
  const { data, error } = await supabase.from("common_profile").select("user_id, social_links").in("user_id", ids);
  if (error) {
    console.warn("Community social links unavailable:", error.message);
    return;
  }
  const links = new Map((data ?? []).map(profile => [profile.user_id, safeSocialLinks(profile.social_links)]));
  for (const row of rows) row.author_social_links = links.get(row.author_id) ?? {};
}

// Shape compatible with api.CommunityPostListItem (no import to avoid circular deps)
function rowToListItem(row: CommunityPostListItemFromSupabase) {
  const author = {
    social_links: row.author_social_links ?? {},
    id: row.author_id,
    username: row.author_username ?? "알 수 없음",
    nickname: row.author_nickname ?? null,
    avatar_url: row.author_avatar_path ? getProfileAvatarUrl(row.author_avatar_path, SUPABASE_URL) : null,
  };
  return {
    id: row.id,
    title: row.title,
    created_at: row.created_at,
    updated_at: row.updated_at,
    hits: row.hits ?? 0,
    top_fixed: row.top_fixed ?? false,
    tags: row.tags,
    category: row.category_legacy,
    category_new: row.category_new_id != null ? { id: row.category_new_id, name: row.category_name } : null,
    subcategory:
      row.subcategory_id != null
        ? { id: row.subcategory_id, name: row.subcategory_name, category_id: row.category_new_id }
        : null,
    author,
    head_image_url: row.head_image ? getSupabaseMediaUrl(row.head_image) : null,
    file_upload_url: row.file_upload ? getSupabaseMediaUrl(row.file_upload) : null,
    file_upload_name: row.file_upload ? row.file_upload.split("/").pop() ?? null : null,
    num_comments: row.num_comments ?? 0,
    num_likes: row.num_likes ?? 0,
    is_liked: false,
    is_bookmarked: false,
    can_edit: false,
    can_delete: false,
    can_pin: false,
    vibe_coding_link: row.vibe_coding_link,
  };
}

export type ListCommunityPostsParams = {
  news?: "exclude" | "only";
  purpose?: CommunityPurpose | "";
  unanswered?: boolean;
  page?: number;
  limit?: number;
  category_id?: number | "";
  subcategory_id?: number | "";
  sort?: "recent" | "popular" | "hits" | "likes";
  /** true: 홈 하이라이트 등 — 고정글 우선 없이 작성일·id만으로 최신순 */
  chronologicalLatest?: boolean;
  q?: string;
  tag?: string;
  author?: number;
  postIds?: number[];
};

const COMMENT_SEARCH_ROW_SCAN_CAP = 8000;
const COMMENT_SEARCH_MAX_DISTINCT_POST_IDS = 2000;

/** Post IDs that have a matching non-deleted comment (for board search OR). Tries RPC first; falls back to view if RPC is not deployed. */
async function postIdsFromCommentSearch(searchTerm: string): Promise<number[]> {
  if (!supabase || searchTerm.trim().length < 2) return [];
  const term = searchTerm.trim();

  const { data: rpcRows, error: rpcError } = await supabase.rpc("community_post_ids_from_comment_search", {
    p_search: term,
  });
  if (!rpcError && rpcRows != null && Array.isArray(rpcRows)) {
    const ids = (rpcRows as { post_id?: number | string }[])
      .map((r) => Number(r.post_id))
      .filter((id) => Number.isFinite(id) && id > 0);
    return [...new Set(ids)];
  }

  const { data, error } = await supabase
    .from("community_comment_list_view")
    .select("post_id")
    .eq("is_deleted", false)
    .ilike("content", `%${term}%`)
    .limit(COMMENT_SEARCH_ROW_SCAN_CAP);

  if (error || !data?.length) return [];

  const seen = new Set<number>();
  for (const row of data as { post_id?: number | string | null }[]) {
    const id = row.post_id == null ? NaN : Number(row.post_id);
    if (!Number.isFinite(id) || id <= 0) continue;
    seen.add(id);
    if (seen.size >= COMMENT_SEARCH_MAX_DISTINCT_POST_IDS) break;
  }
  return [...seen];
}

/**
 * List community posts from Supabase (anon). Returns null if Supabase unavailable or error.
 */
export async function listCommunityPostsFromSupabase(params: ListCommunityPostsParams): Promise<{
  results: ReturnType<typeof rowToListItem>[];
  count: number;
  next: string | null;
  previous: string | null;
} | null> {
  if (!supabase) return null;

  const page = params.page ?? 1;
  const limit = Math.min(params.limit ?? 20, 60);
  const from = (page - 1) * limit;
  const to = from + limit - 1;

  let query = supabase
    .from("community_post_list_view")
    .select(
      "id, title, created_at, updated_at, hits, top_fixed, tags, category_legacy, category_new_id, subcategory_id, author_id, author_username, author_nickname, author_avatar_path, category_name, subcategory_name, num_comments, num_likes, head_image, file_upload, vibe_coding_link, popularity_score",
      { count: "exact" }
    );

  if (params.news === "exclude") query = query.or("subcategory_name.is.null,subcategory_name.neq.교육뉴스");
  if (params.news === "only") query = query.eq("subcategory_name", "교육뉴스");
  if (params.purpose) query = query.or(COMMUNITY_PURPOSE_FILTERS[params.purpose]);
  if (params.unanswered) query = query.eq("num_comments", 0).eq("top_fixed", false);
  if (typeof params.category_id === "number") query = query.eq("category_new_id", params.category_id);
  if (typeof params.subcategory_id === "number") query = query.eq("subcategory_id", params.subcategory_id);
  if (params.author) query = query.eq("author_id", params.author);
  if (params.postIds && params.postIds.length > 0) {
    query = query.in("id", params.postIds);
  } else if (params.postIds && params.postIds.length === 0) {
    return { results: [], count: 0, next: null, previous: null };
  }
  if (params.tag && params.tag.trim()) query = query.ilike("tags", `%${params.tag.trim()}%`);
  if (params.q && params.q.trim().length >= 2) {
    const term = params.q.trim();
    const commentPostIds = await postIdsFromCommentSearch(term);
    const textMatch = `title.ilike.%${term}%,content.ilike.%${term}%,tags.ilike.%${term}%`;
    if (commentPostIds.length > 0) {
      query = query.or(`${textMatch},id.in.(${commentPostIds.join(",")})`);
    } else {
      query = query.or(textMatch);
    }
  }
  if (params.sort === "popular") {
    // Popular posts only consider recent 6 months.
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    query = query.gte("created_at", sixMonthsAgo.toISOString());
  }

  if (params.sort === "popular")
    query = query.order("popularity_score", { ascending: false }).order("created_at", { ascending: false });
  else if (params.sort === "hits")
    query = query.order("top_fixed", { ascending: false }).order("hits", { ascending: false }).order("created_at", { ascending: false });
  else if (params.sort === "likes")
    query = query.order("top_fixed", { ascending: false }).order("num_likes", { ascending: false }).order("created_at", { ascending: false });
  else if (params.chronologicalLatest)
    query = query.order("created_at", { ascending: false }).order("id", { ascending: false });
  else
    query = query.order("top_fixed", { ascending: false }).order("created_at", { ascending: false });

  const { data, error, count } = await query.range(from, to);

  if (error || data == null) return null;

  await addAuthorSocialLinks(data as CommunityPostListItemFromSupabase[]);
  const total = count ?? 0;
  const results = (data as CommunityPostListItemFromSupabase[]).map(rowToListItem);
  const next = from + data.length < total ? String(page + 1) : null;
  const previous = page > 1 ? String(page - 1) : null;

  return { results, count: total, next, previous };
}

/**
 * Single post detail from Supabase (anon). Returns null if not found or error.
 */
export async function getCommunityPostDetailFromSupabase(postId: number): Promise<(ReturnType<typeof rowToListItem> & {
  content: string;
  additional_files: { id: number; filename: string; url: string | null; uploaded_at: string }[];
  topic_id: number | null;
  grade: number | null;
}) | null> {
  if (!supabase) return null;

  const { data: row, error } = await supabase
    .from("community_post_list_view")
    .select("*")
    .eq("id", postId)
    .single();

  if (error || !row) return null;

  await addAuthorSocialLinks([row as CommunityPostListItemFromSupabase]);
  const listItem = rowToListItem(row as CommunityPostListItemFromSupabase);
  const myUserId = await getMyDjangoUserId();

  const [{ data: files }, likeRes, bookmarkRes] = await Promise.all([
    supabase
      .from("tboard_postfile")
      .select("id, filename, file, uploaded_at")
      .eq("post_id", postId)
      .order("uploaded_at", { ascending: true }),
    myUserId
      ? supabase
          .from("tboard_post_like_users")
          .select("id")
          .eq("post_id", postId)
          .eq("user_id", myUserId)
          .maybeSingle()
      : Promise.resolve({ data: null }),
    myUserId
      ? supabase
          .from("tboard_postbookmark")
          .select("id")
          .eq("post_id", postId)
          .eq("user_id", myUserId)
          .maybeSingle()
      : Promise.resolve({ data: null }),
  ]);

  const additional_files = (files ?? []).map((f: { id: number; filename: string; file: string | null; uploaded_at: string }) => ({
    id: f.id,
    filename: f.filename || (f.file ? f.file.split("/").pop() ?? "" : ""),
    url: f.file ? getSupabaseMediaUrl(f.file) : null,
    uploaded_at: f.uploaded_at,
  }));

  const isMine = !!myUserId && listItem.author.id === myUserId;

  return {
    ...listItem,
    is_liked: !!likeRes.data,
    is_bookmarked: !!bookmarkRes.data,
    can_edit: isMine,
    can_delete: isMine,
    content: (row as { content?: string }).content ?? "",
    additional_files,
    topic_id: (row as { topic_id?: number }).topic_id ?? null,
    grade: (row as { grade?: number }).grade ?? null,
  };
}

const VIEWED_POSTS_KEY = "community_post_viewed";

/**
 * Increment post hit count in Supabase (Vercel direct). Called when post is loaded from Supabase.
 * Dedupes by sessionStorage so same post only counts once per session.
 */
export async function incrementCommunityPostHit(postId: number): Promise<void> {
  if (!supabase) return;
  try {
    let viewed: number[] = [];
    try {
      const raw = sessionStorage.getItem(VIEWED_POSTS_KEY);
      if (raw) viewed = JSON.parse(raw);
    } catch {
      viewed = [];
    }
    if (viewed.includes(postId)) return;
    viewed.push(postId);
    if (viewed.length > 200) viewed = viewed.slice(-100);
    try {
      sessionStorage.setItem(VIEWED_POSTS_KEY, JSON.stringify(viewed));
    } catch {
      // ignore
    }
    await supabase.rpc("increment_community_post_hit", { post_id: postId });
  } catch {
    // ignore
  }
}

// ─── Post like toggle via Supabase RPC ────────────────────────────────────────

export async function toggleCommunityPostLikeFromSupabase(
  postId: number
): Promise<{ liked: boolean; num_likes: number } | null> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc("toggle_community_post_like", {
      p_post_id: postId,
    });
    if (error || !data || !Array.isArray(data) || !data[0]) {
      return null;
    }
    const row = data[0] as { liked: boolean; num_likes: number };
    return { liked: !!row.liked, num_likes: Number(row.num_likes ?? 0) };
  } catch (e) {
    console.warn("Supabase toggle_community_post_like failed:", e);
    return null;
  }
}

export async function toggleCommunityCommentLikeFromSupabase(
  commentId: number
): Promise<{ liked: boolean; num_likes: number } | null> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc("toggle_community_comment_like", {
      p_comment_id: commentId,
    });
    if (error || !data || !Array.isArray(data) || !data[0]) {
      return null;
    }
    const row = data[0] as { liked: boolean; num_likes: number };
    return { liked: !!row.liked, num_likes: Number(row.num_likes ?? 0) };
  } catch (e) {
    console.warn("Supabase toggle_community_comment_like failed:", e);
    return null;
  }
}

// ─── Post bookmark toggle via Supabase RPC ────────────────────────────────────

export async function toggleCommunityPostBookmarkFromSupabase(
  postId: number
): Promise<{ bookmarked: boolean; my_bookmark_count: number } | null> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc("toggle_community_post_bookmark", {
      p_post_id: postId,
    });
    if (error || !data || !Array.isArray(data) || !data[0]) {
      // Expose server error message to caller so UI can instruct re-login if mapping is missing.
      console.warn("Supabase toggle_community_post_bookmark error:", error);
      return null;
    }
    const row = data[0] as { bookmarked: boolean; my_bookmark_count: number };
    return {
      bookmarked: !!row.bookmarked,
      my_bookmark_count: Number(row.my_bookmark_count ?? 0),
    };
  } catch (e) {
    console.warn("Supabase toggle_community_post_bookmark failed:", e);
    return null;
  }
}

// ─── User block toggle via Supabase RPC ───────────────────────────────────────

export async function toggleCommunityUserBlockFromSupabase(
  targetUserId: number
): Promise<{ blocked: boolean } | null> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc("toggle_community_user_block", {
      p_target_user_id: targetUserId,
    });
    if (error || !data || !Array.isArray(data) || !data[0]) {
      return null;
    }
    const row = data[0] as { blocked: boolean };
    return { blocked: !!row.blocked };
  } catch (e) {
    console.warn("Supabase toggle_community_user_block failed:", e);
    return null;
  }
}

// ─── User follow toggle via Supabase RPC ──────────────────────────────────────

export async function toggleCommunityUserFollowFromSupabase(
  targetUserId: number
): Promise<{ followed: boolean } | null> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc("toggle_community_user_follow", {
      p_target_user_id: targetUserId,
    });
    if (error || !data || !Array.isArray(data) || !data[0]) {
      return null;
    }
    const row = data[0] as { followed: boolean };
    return { followed: !!row.followed };
  } catch (e) {
    console.warn("Supabase toggle_community_user_follow failed:", e);
    return null;
  }
}

// ─── Post / Comment report via Supabase RPC ───────────────────────────────────

export async function reportCommunityPostFromSupabase(
  postId: number,
  reason: string
): Promise<{ reported: boolean } | null> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc("report_community_post", {
      p_post_id: postId,
      p_reason: reason ?? "",
    });
    if (error || !data || !Array.isArray(data) || !data[0]) {
      return null;
    }
    const row = data[0] as { reported: boolean };
    return { reported: !!row.reported };
  } catch (e) {
    console.warn("Supabase report_community_post failed:", e);
    return null;
  }
}

export async function reportCommunityCommentFromSupabase(
  commentId: number,
  reason: string
): Promise<{ reported: boolean } | null> {
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc("report_community_comment", {
      p_comment_id: commentId,
      p_reason: reason ?? "",
    });
    if (error || !data || !Array.isArray(data) || !data[0]) {
      return null;
    }
    const row = data[0] as { reported: boolean };
    return { reported: !!row.reported };
  } catch (e) {
    console.warn("Supabase report_community_comment failed:", e);
    return null;
  }
}

// ─── Comments (for post detail) from view community_comment_list_view ────────

type CommentRow = {
  id: number;
  post_id: number;
  parent_id: number | null;
  content: string;
  is_deleted: boolean;
  created_at: string;
  modified_at: string | null;
  author_id: number | null;
  author_username: string | null;
  author_nickname: string | null;
  author_avatar_path: string | null;
  author_social_links?: SocialLinks;
  comment_image: string | null;
  comment_file: string | null;
  num_likes: number | null;
  is_liked: boolean | null;
};

/** Nested comment shape for post detail (paths + URLs for normalizeCommunityComment) */
export type CommunityCommentListNode = {
  id: number;
  post_id: number;
  parent_id: number | null;
  content: string;
  is_deleted: boolean;
  created_at: string;
  modified_at: string | null;
  created_string: string | null;
  author: {
    social_links?: SocialLinks;
    id: number | null;
    username: string;
    nickname: string | null;
    avatar_url: string | null;
  };
  /** DB storage path — prefer over *_url in normalizeCommunityComment */
  comment_image: string | null;
  comment_file: string | null;
  comment_image_url: string | null;
  comment_file_url: string | null;
  comment_file_name: string | null;
  replies: CommunityCommentListNode[];
  can_edit: boolean;
  can_delete: boolean;
  num_likes: number;
  is_liked: boolean;
};

/** Build nested replies from flat list */
function buildCommentTree(rows: CommentRow[], parentId: number | null): CommunityCommentListNode[] {
  return rows
    .filter((r) => (parentId === null ? r.parent_id == null : r.parent_id === parentId))
    .map((r) => ({
      id: r.id,
      post_id: r.post_id,
      parent_id: r.parent_id,
      content: r.content,
      is_deleted: r.is_deleted ?? false,
      created_at: r.created_at,
      modified_at: r.modified_at,
      created_string: null,
      author: {
        social_links: r.is_deleted ? {} : r.author_social_links ?? {},
        id: r.author_id,
        username: r.author_username ?? "알 수 없음",
        nickname: r.author_nickname ?? null,
        avatar_url: r.author_avatar_path ? getProfileAvatarUrl(r.author_avatar_path, SUPABASE_URL) : null,
      },
      comment_image: r.comment_image,
      comment_file: r.comment_file,
      comment_image_url: r.comment_image ? getSupabaseMediaUrl(r.comment_image) : null,
      comment_file_url: r.comment_file ? getSupabaseMediaUrl(r.comment_file) : null,
      comment_file_name: r.comment_file ? r.comment_file.split("/").pop() ?? null : null,
      replies: buildCommentTree(rows, r.id),
      can_edit: false,
      can_delete: false,
      num_likes: Number(r.num_likes ?? 0),
      is_liked: Boolean(r.is_liked),
    }));
}

/**
 * List comments for a post from Supabase (anon). Returns null if Supabase unavailable or error.
 */
export async function listCommunityCommentsFromSupabase(
  postId: number
): Promise<CommunityCommentListNode[] | null> {
  if (!supabase) return null;

  const { data, error } = await supabase
    .from("community_comment_list_view")
    .select("*")
    .eq("post_id", postId)
    .order("created_at", { ascending: true });

  if (error || data == null) return null;

  await addAuthorSocialLinks(data as CommentRow[]);
  return buildCommentTree(data as CommentRow[], null);
}

// ─── Drafts ───────────────────────────────────────────────────────────────────

export async function saveCommunityDraft(draftKey: string, payload: any): Promise<void> {
  if (!supabase) throw new Error("임시저장 연결을 사용할 수 없습니다.");
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !session?.user?.id) throw new Error("로그인 상태를 확인한 후 다시 저장해 주세요.");
  const { error } = await supabase.from("community_drafts").upsert({
    user_id: session.user.id, draft_key: draftKey, payload,
  }, { onConflict: "user_id, draft_key" });
  if (error) throw new Error("글을 임시저장하지 못했습니다. 작성 내용을 복사해 보관해 주세요.");
}

export async function getCommunityDraft(draftKey: string): Promise<any | null> {
  if (!supabase) throw new Error("임시저장 연결을 사용할 수 없습니다.");
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !session?.user?.id) throw new Error("로그인 상태를 확인해 주세요.");
  const { data, error } = await supabase.from("community_drafts").select("payload")
    .eq("user_id", session.user.id).eq("draft_key", draftKey).maybeSingle();
  if (error) throw new Error("임시저장한 글을 확인하지 못했습니다.");
  return data?.payload ?? null;
}

export async function deleteCommunityDraft(draftKey: string): Promise<void> {
  if (!supabase) throw new Error("임시저장 연결을 사용할 수 없습니다.");
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError || !session?.user?.id) throw new Error("로그인 상태를 확인해 주세요.");
  const { error } = await supabase.from("community_drafts").delete()
    .eq("user_id", session.user.id).eq("draft_key", draftKey);
  if (error) throw new Error("임시저장한 글을 지우지 못했습니다.");
}

// ─── Post Write (Create, Update, Delete) ──────────────────────────────────────

export async function uploadCommunityMedia(file: File, folder: "images" | "files"): Promise<string | null> {
  if (!supabase) return null;
  const ext = file.name.split('.').pop() || '';
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const date = String(now.getDate()).padStart(2, '0');
  const uuid = Math.random().toString(36).substring(2, 15);
  const path = `tboard/${folder}/${year}/${month}/${date}/${uuid}.${ext}`;
  
  const { data, error } = await supabase.storage.from("foreducator-media").upload(path, file);
  if (error || !data) {
    console.error("Media upload error", error);
    return null;
  }
  return data.path;
}

export type CreateCommunityPostData = {
  content_blocks?: CommunityContentBlock[];
  title: string;
  content: string;
  category_new_id?: number | null;
  subcategory_id?: number | null;
  topic_id?: number | null;
  grade?: number | null;
  tags?: string;
  top_fixed?: boolean;
  vibe_coding_link?: string | null;
  head_image?: File | null;
  file_upload?: File | null;
  additional_files?: File[];
};

export async function listFollowingCommunityPostsFromSupabase(userId: string, limit = 10) {
  if (!supabase) return null;
  const { data, error } = await supabase.rpc("get_following_posts", { p_user_id: userId, p_limit: limit });
  if (error) {
    console.error("Supabase RPC error fetching following posts:", error);
    return { results: [] };
  }
  return { results: data || [] };
}

export async function createCommunityPostFromSupabase(data: CreateCommunityPostData): Promise<{ id: number } | null> {
  if (!supabase) return null;

  let headImgPath = null;
  let mainFilePath = null;
  
  if (data.head_image) headImgPath = await uploadCommunityMedia(data.head_image, "images");
  if (data.file_upload) mainFilePath = await uploadCommunityMedia(data.file_upload, "files");

  // lazy load images in content (standard practice here)
  let content = data.content;
  if (content && typeof window !== 'undefined') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const imgs = doc.querySelectorAll('img');
    imgs.forEach(img => {
      if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
    });
    content = doc.body.innerHTML;

  }

  const { data: dUserId } = await supabase.rpc('django_user_id');
  if (!dUserId) {
    console.warn("No Supabase–Django user mapping; cannot create post without mapped auth_user.id.");
    return null;
  }

  const insertPayload = {
    title: data.title,
    content: content,
    category_new_id: data.category_new_id || null,
    subcategory_id: data.subcategory_id || null,
    topic_id: data.topic_id || null,
    grade: data.grade || null,
    tags: data.tags || null,
    top_fixed: data.top_fixed || false,
    vibe_coding_link: data.vibe_coding_link || null,
    head_image: headImgPath,
    category: data.topic_id === 999 ? "인증" : "자유",
    file_upload: mainFilePath,
    author_id: dUserId || undefined,
  };
  const { data: postLine, error: postError } = data.content_blocks !== undefined
    ? {data:await contentRequest<{id:number}>('save',{payload:insertPayload,blocks:data.content_blocks},'POST'),error:null}
    : await supabase.from('tboard_post').insert(insertPayload).select('id').single();

  if (postError || !postLine) {
    console.error("Supabase post creation failed", postError);
    return null;
  }

  if (data.additional_files && data.additional_files.length > 0) {
    for (const f of data.additional_files) {
      const p = await uploadCommunityMedia(f, "files");
      if (p) {
        await supabase.from('tboard_postfile').insert({
          post_id: postLine.id,
          file: p,
          filename: f.name
        });
      }
    }
  }

  return { id: postLine.id };
}

export async function updateCommunityPostFromSupabase(
  postId: number, 
  data: Partial<CreateCommunityPostData>, 
  deleted_file_ids?: number[]
): Promise<{ success: boolean }> {
  if (!supabase) return { success: false };

  const { data: existingPost } = await supabase
    .from("tboard_post")
    .select("head_image, file_upload")
    .eq("id", postId)
    .maybeSingle();

  let headImgPath: string | undefined = undefined;
  let mainFilePath: string | undefined = undefined;
  
  if (data.head_image) headImgPath = await uploadCommunityMedia(data.head_image, "images") || undefined;
  if (data.file_upload) mainFilePath = await uploadCommunityMedia(data.file_upload, "files") || undefined;

  let content = data.content;
  if (content && typeof window !== 'undefined') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/html');
    const imgs = doc.querySelectorAll('img');
    imgs.forEach(img => {
      if (!img.hasAttribute('loading')) img.setAttribute('loading', 'lazy');
    });
    content = doc.body.innerHTML;
  }

  const updatePayload: any = {};
  if (data.title !== undefined) updatePayload.title = data.title;
  if (content !== undefined) updatePayload.content = content;
  if (data.category_new_id !== undefined) updatePayload.category_new_id = data.category_new_id;
  if (data.subcategory_id !== undefined) updatePayload.subcategory_id = data.subcategory_id;
  if (data.topic_id !== undefined) updatePayload.topic_id = data.topic_id;
  if (data.grade !== undefined) updatePayload.grade = data.grade;
  if (data.tags !== undefined) updatePayload.tags = data.tags;
  if (data.top_fixed !== undefined) updatePayload.top_fixed = data.top_fixed;
  if (data.vibe_coding_link !== undefined) updatePayload.vibe_coding_link = data.vibe_coding_link;
  if (headImgPath !== undefined) updatePayload.head_image = headImgPath;
  if (mainFilePath !== undefined) updatePayload.file_upload = mainFilePath;
  if (data.topic_id === 999) updatePayload.category = "인증";

  if (data.content_blocks !== undefined) {
    await contentRequest('save',{postId,payload:updatePayload,blocks:data.content_blocks},'POST');
  } else if (Object.keys(updatePayload).length > 0) {
    const { error } = await supabase.from('tboard_post').update(updatePayload).eq('id', postId);
    if (error) { console.error("Update post failed", error); return {success:false}; }
  }

  if (headImgPath && existingPost?.head_image) {
    try {
      await removeForeducatorMediaObjects([existingPost.head_image]);
    } catch (e) {
      console.warn("Community post: old head_image storage delete failed", e);
    }
  }
  if (mainFilePath && existingPost?.file_upload) {
    try {
      await removeForeducatorMediaObjects([existingPost.file_upload]);
    } catch (e) {
      console.warn("Community post: old file_upload storage delete failed", e);
    }
  }

  if (data.additional_files && data.additional_files.length > 0) {
    for (const f of data.additional_files) {
      const p = await uploadCommunityMedia(f, "files");
      if (p) {
        await supabase.from('tboard_postfile').insert({
          post_id: postId,
          file: p,
          filename: f.name
        });
      }
    }
  }

  if (deleted_file_ids && deleted_file_ids.length > 0) {
    const { data: fileRows } = await supabase
      .from("tboard_postfile")
      .select("file")
      .eq("post_id", postId)
      .in("id", deleted_file_ids);
    const toRemove = (fileRows ?? []).map((r: { file: string | null }) => r.file).filter(Boolean);
    if (toRemove.length > 0) {
      try {
        await removeForeducatorMediaObjects(toRemove);
      } catch (e) {
        console.warn("Community post: attachment storage delete failed", e);
      }
    }
    for (const fid of deleted_file_ids) {
      await supabase.from('tboard_postfile').delete().eq('id', fid);
    }
  }

  return { success: true };
}

export async function deleteCommunityPostFromSupabase(postId: number): Promise<{ success: boolean }> {
  if (!supabase) return { success: false };
  const { data: post } = await supabase
    .from("tboard_post")
    .select("head_image, file_upload, content")
    .eq("id", postId)
    .maybeSingle();
  const { data: extraFiles } = await supabase
    .from("tboard_postfile")
    .select("file")
    .eq("post_id", postId);
  const paths = [
    post?.head_image,
    post?.file_upload,
    ...extractForeducatorMediaKeysFromHtml(post?.content),
    ...((extraFiles ?? []) as { file: string | null }[]).map((r) => r.file),
  ];
  try {
    await removeForeducatorMediaObjects(paths);
  } catch (e) {
    console.error("Delete post: storage cleanup failed", e);
    return { success: false };
  }
  // delete_community_post RPC: SECURITY DEFINER 함수로 댓글/좋아요/북마크 등
  // 연관 데이터를 cascade 삭제 후 게시글을 삭제합니다.
  const { error } = await supabase.rpc("delete_community_post", { p_post_id: postId });
  if (error) {
    console.error("Delete post failed", error);
    return { success: false };
  }
  return { success: true };
}

// ─── My Activity (Me APIs) ────────────────────────────────────────────────────

export async function getMyDjangoUserId(): Promise<number | null> {
  try {
    const cached = localStorage.getItem('user_data');
    if (cached) {
      const user = JSON.parse(cached);
      if (typeof user.id === 'number' && user.id > 0) return user.id;
    }
  } catch {}
  // localStorage 없을 때(세션만 있는 경우) Supabase 매핑으로 보강
  if (!supabase) return null;
  try {
    const { data, error } = await supabase.rpc("django_user_id");
    if (error) return null;
    const id = typeof data === "number" ? data : Number(data);
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export async function listMyCommunityPostsFromSupabase(params: { page?: number; limit?: number }) {
  const authorId = await getMyDjangoUserId();
  if (!authorId) return null;
  return listCommunityPostsFromSupabase({ ...params, author: authorId });
}

export async function listMyCommunityLikesFromSupabase(params: { page?: number; limit?: number }) {
  const authorId = await getMyDjangoUserId();
  if (!authorId || !supabase) return null;
  const page = params.page ?? 1;
  const limit = Math.min(params.limit ?? 20, 60);
  const from = (page - 1) * limit;
  const to = from + limit - 1;

  const { data: likes, count, error } = await supabase
    .from('tboard_post_like_users')
    .select('post_id', { count: 'exact' })
    .eq('user_id', authorId)
    .range(from, to);

  if (error || !likes || likes.length === 0) return { results: [], count: count ?? 0, next: null, previous: null };
  
  const postIds = likes.map(l => l.post_id);
  const postsRes = await listCommunityPostsFromSupabase({ postIds, page: 1, limit: 100 });
  if (!postsRes) return null;

  const postMap = new Map(postsRes.results.map(p => [p.id, p]));
  const results = postIds.map(id => postMap.get(id)).filter(Boolean) as any[];

  return { results, count: count ?? 0, next: from + likes.length < (count ?? 0) ? String(page + 1) : null, previous: page > 1 ? String(page - 1) : null };
}

export async function listMyCommunityCommentedFromSupabase(params: { page?: number; limit?: number }) {
  const authorId = await getMyDjangoUserId();
  if (!authorId || !supabase) return null;
  const page = params.page ?? 1;
  const limit = Math.min(params.limit ?? 20, 60);
  const from = (page - 1) * limit;
  const to = from + limit - 1;

  // Supabase distinct isn't natively supported beautifully without RPC, but we can just grab comments ordered by created_at 
  // and deduplicate in JS? Or just use raw queries. 
  // Let's just fetch comments paginated and then deduplicate. This might not map 1:1 with Django's distinct, but it's close enough.
  const { data: comments, count, error } = await supabase
    .from('tboard_comment')
    .select('post_id', { count: 'exact' })
    .eq('author_id', authorId)
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error || !comments || comments.length === 0) return { results: [], count: count ?? 0, next: null, previous: null };
  
  // Deduplicate
  const postIds = Array.from(
    new Set(
      (comments as any[])
        .map((c) => c?.post_id)
        .filter((id) => typeof id === "number" && Number.isFinite(id))
    )
  );
  if (postIds.length === 0) return { results: [], count: count ?? 0, next: null, previous: null };
  const postsRes = await listCommunityPostsFromSupabase({ postIds, page: 1, limit: 100 });
  if (!postsRes) return null;

  const postMap = new Map(postsRes.results.map(p => [p.id, p]));
  const results = postIds.map(id => postMap.get(id)).filter(Boolean) as any[];

  return { results, count: count ?? 0, next: from + comments.length < (count ?? 0) ? String(page + 1) : null, previous: page > 1 ? String(page - 1) : null };
}

export async function listMyCommunityBookmarksFromSupabase(params: { page?: number; limit?: number }) {
  const authorId = await getMyDjangoUserId();
  if (!authorId || !supabase) return null;
  const page = params.page ?? 1;
  const limit = Math.min(params.limit ?? 20, 60);
  const from = (page - 1) * limit;
  const to = from + limit - 1;

  const { data: bookmarks, count, error } = await supabase
    .from('tboard_postbookmark')
    .select('post_id', { count: 'exact' })
    .eq('user_id', authorId)
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error || !bookmarks || bookmarks.length === 0) return { results: [], count: count ?? 0, next: null, previous: null };
  
  const postIds = bookmarks.map(b => b.post_id);
  const postsRes = await listCommunityPostsFromSupabase({ postIds, page: 1, limit: 100 });
  if (!postsRes) return null;

  const postMap = new Map(postsRes.results.map(p => [p.id, p]));
  const results = postIds.map(id => postMap.get(id)).filter(Boolean) as any[];

  return { results, count: count ?? 0, next: from + bookmarks.length < (count ?? 0) ? String(page + 1) : null, previous: page > 1 ? String(page - 1) : null };
}

// ─── Comment Write (Create, Update, Delete) ───────────────────────────────────

export async function createCommunityCommentFromSupabase(
  postId: number, 
  data: { content: string; parent_id?: number | null; comment_image?: File | null; comment_file?: File | null }
): Promise<{ id: number; [key: string]: any } | null> {
  if (!supabase) return null;

  let imgPath: string | null = null;
  let filePath: string | null = null;

  if (data.comment_image) {
    imgPath = await uploadCommunityMedia(data.comment_image, "images");
    if (!imgPath) return null;
  }
  if (data.comment_file) {
    filePath = await uploadCommunityMedia(data.comment_file, "files");
    if (!filePath) return null;
  }

  const { data: row, error } = await supabase.from('tboard_comment').insert({
    post_id: postId,
    content: data.content,
    parent_id: data.parent_id || null,
    comment_image: imgPath,
    comment_file: filePath,
  }).select('*').single();

  if (error || !row) {
    console.error("Create comment failed", error);
    return null;
  }
  return row;
}

export async function updateCommunityCommentFromSupabase(
  commentId: number, 
  data: { content?: string; comment_image?: File | null; comment_file?: File | null }
): Promise<{ id: number; [key: string]: any } | null> {
  if (!supabase) return null;

  const updatePayload: Record<string, unknown> = {};
  if (data.content !== undefined) updatePayload.content = data.content;
  if (data.comment_image) {
    const p = await uploadCommunityMedia(data.comment_image, "images");
    if (!p) return null;
    updatePayload.comment_image = p;
  }
  if (data.comment_file) {
    const p = await uploadCommunityMedia(data.comment_file, "files");
    if (!p) return null;
    updatePayload.comment_file = p;
  }

  if (Object.keys(updatePayload).length > 0) {
    const { data: row, error } = await supabase.from('tboard_comment').update(updatePayload).eq('id', commentId).select('*').single();
    if (error || !row) {
      console.error("Update comment failed", error);
      return null;
    }
    return row;
  }
  return null;
}

export async function deleteCommunityCommentFromSupabase(commentId: number): Promise<{ success: boolean }> {
  if (!supabase) return { success: false };
  // Soft delete in Django was: is_deleted = True, deleted = True
  const { error } = await supabase.from('tboard_comment').update({
    is_deleted: true,
    deleted: true,
    content: "삭제된 댓글입니다"
  }).eq('id', commentId);
  
  if (error) {
    console.error("Delete comment failed", error);
    return { success: false };
  }
  return { success: true };
}

// ─── Community Editor Image Upload ──────────────────────────────────────────

export async function uploadCommunityEditorImageFromSupabase(file: File): Promise<{ url: string } | null> {
  if (!supabase) return null;
  const ext = file.name.split('.').pop() || '';
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const date = String(now.getDate()).padStart(2, '0');
  const uuid = Math.random().toString(36).substring(2, 15);
  const path = `community/rich-text-editor/${year}/${month}/${date}/${uuid}.${ext}`;
  
  const { data, error } = await supabase.storage.from("foreducator-media").upload(path, file);
  if (error || !data) {
    console.error("Editor image upload error", error);
    return null;
  }
  return { url: getSupabaseMediaUrl(data.path) };
}

// ─── Community User Profiles & Follows & Memos ──────────────────────────────

export async function getCommunityUserProfileFromSupabase(userId: number) {
  if (!supabase) return null;
  
  // 1) Get basic user info
  const { data: userData } = await supabase.from("auth_user").select("username").eq("id", userId).single();
  const { data: profileData } = await supabase.from("common_profile").select("*").eq("user_id", userId).single();
  
  if (!userData) return null;

  const profileId = profileData?.id;
  let followers_count = 0;
  let following_count = 0;

  if (profileId) {
    // 2) Get follower / following counts
    const [{ count: f1 }, { count: f2 }] = await Promise.all([
      supabase.from("common_follow").select("id", { count: "exact", head: true }).eq("following_id", profileId),
      supabase.from("common_follow").select("id", { count: "exact", head: true }).eq("follower_id", profileId)
    ]);
    followers_count = f1 || 0;
    following_count = f2 || 0;
  }

  // 3) My context (memo & is_following)
  let memo = null;
  let is_following = false;
  
  const myUserId = await getMyDjangoUserId();
  if (myUserId && myUserId !== userId) {
    const { data: memoData } = await supabase
      .from("common_memo")
      .select("content, color")
      .eq("author_id", myUserId)
      .eq("target_id", userId)
      .maybeSingle();
    if (memoData) memo = memoData;

    if (profileId) {
      const { data: myProfile } = await supabase.from("common_profile").select("id").eq("user_id", myUserId).maybeSingle();
      if (myProfile?.id) {
        const { count: isF } = await supabase.from("common_follow").select("id", { count: "exact", head: true }).eq("follower_id", myProfile.id).eq("following_id", profileId);
        is_following = (isF || 0) > 0;
      }
    }
  }

  const nickname = profileData?.nickname || userData.username;
  const avatarUrl = getProfileAvatarUrl(profileData?.avatar, SUPABASE_URL);

  return {
    nickname,
    bio: profileData?.bio || "",
    avatar_url: avatarUrl || null,
    followers_count,
    following_count,
    activity_points: profileData?.activity_points || 0,
    memo,
    is_following,
    username: userData.username,
  };
}

export async function getCommunityUserMemoFromSupabase(userId: number): Promise<{ content: string | null; color: string | null } | null> {
  if (!supabase) return null;
  const myUserId = await getMyDjangoUserId();
  if (!myUserId) return null;

  const { data, error } = await supabase
    .from("common_memo")
    .select("content, color")
    .eq("author_id", myUserId)
    .eq("target_id", userId)
    .maybeSingle();
  if (error) throw new Error(error.message || "메모를 불러오지 못했습니다.");
  if (!data) return { content: null, color: null };
  return data;
}

export async function setCommunityUserMemoFromSupabase(userId: number, data: { content: string; color?: string }) {
  if (!supabase) throw new Error("연결을 확인할 수 없습니다.");
  const myUserId = await getMyDjangoUserId();
  if (!myUserId) throw new Error("로그인 후 이용해 주세요.");

  const { data: existing } = await supabase
    .from("common_memo")
    .select("id")
    .eq("author_id", myUserId)
    .eq("target_id", userId)
    .maybeSingle();

  const payload = {
    author_id: myUserId,
    target_id: userId,
    content: data.content,
    color: data.color || "#FF0000",
  };

  let res;
  if (existing) {
    res = await supabase.from("common_memo").update(payload).eq("id", existing.id).select().single();
  } else {
    res = await supabase.from("common_memo").insert([payload]).select().single();
  }

  if (res.error) throw new Error(res.error.message || "메모를 저장하지 못했습니다.");
  if (!res.data) throw new Error("메모를 저장하지 못했습니다.");
  return { content: res.data.content, color: res.data.color };
}

export async function listCommunityBlocksFromSupabase() {
  if (!supabase) return null;
  const myUserId = await getMyDjangoUserId();
  if (!myUserId) return null;

  // FK join 이름이 DB 버전마다 달라 400이 발생할 수 있으므로, 단순 컬럼만 선택하고
  // auth_user 조회는 아래에서 별도 쿼리로 처리합니다.
  const { data, error } = await supabase
    .from("tboard_userblock")
    .select("created_at, blocked_user_id")
    .eq("user_id", myUserId)
    .order("created_at", { ascending: false });

  if (error || !data) return null;
  if (data.length === 0) return { results: [] };

  const userIds = data.map((d: any) => d.blocked_user_id);

  const [{ data: profiles }, { data: users }] = await Promise.all([
    supabase.from("common_profile").select("user_id, nickname, avatar").in("user_id", userIds),
    supabase.from("auth_user").select("id, username").in("id", userIds),
  ]);

  const profileMap = (profiles || []).reduce((acc: any, p: any) => {
    acc[p.user_id] = p;
    return acc;
  }, {});
  const userMap = (users || []).reduce((acc: any, u: any) => {
    acc[u.id] = u;
    return acc;
  }, {});

  const results = data.map((d: any) => {
    const p = profileMap[d.blocked_user_id];
    const u = userMap[d.blocked_user_id];
    return {
      id: d.blocked_user_id,
      username: u?.username || null,
      nickname: p?.nickname || u?.username || null,
      avatar_url: getProfileAvatarUrl(p?.avatar, SUPABASE_URL) || null,
      blocked_at: d.created_at,
    };
  });

  return { results };
}

// Followers and Following views:

export async function getCommunityUserFollowersFromSupabase(userId: number) {
  if (!supabase) return null;
  const myUserId = await getMyDjangoUserId();

  const { data: profile } = await supabase.from("common_profile").select("id").eq("user_id", userId).single();
  if (!profile) return { results: [] };

  const { data: follows, error } = await supabase
    .from("common_follow")
    .select("follower_id")
    .eq("following_id", profile.id)
    .order("created", { ascending: false });

  if (error || !follows) return { results: [] };

  const followerObjIds = follows.map(f => f.follower_id);
  if (followerObjIds.length === 0) return { results: [] };

  const { data: profiles } = await supabase.from("common_profile").select("id, user_id, nickname, bio, avatar").in("id", followerObjIds);
  const profileMap = (profiles || []).reduce((acc: any, p: any) => { acc[p.id] = p; return acc; }, {});
  
  const userIds = profiles?.map(p => p.user_id) || [];
  const { data: users } = await supabase.from("auth_user").select("id, username").in("id", userIds);
  const userMap = (users || []).reduce((acc: any, u: any) => { acc[u.id] = u; return acc; }, {});

  // My follows
  let myFollowingSet = new Set<number>();
  let myMemosMap: any = {};
  if (myUserId) {
    const { data: myProfile } = await supabase.from("common_profile").select("id").eq("user_id", myUserId).single();
    if (myProfile) {
      const { data: myFollows } = await supabase.from("common_follow").select("following_id").eq("follower_id", myProfile.id).in("following_id", followerObjIds);
      (myFollows || []).forEach(f => myFollowingSet.add(f.following_id));
    }
    const { data: myMemos } = await supabase.from("common_memo").select("target_id, content, color").eq("author_id", myUserId).in("target_id", userIds);
    (myMemos || []).forEach(m => { myMemosMap[m.target_id] = { content: m.content, color: m.color }; });
  }

  const results = follows.map(f => {
    const p = profileMap[f.follower_id];
    if (!p) return null;
    const u = userMap[p.user_id];
    return {
      id: p.user_id,
      nickname: p.nickname || u?.username,
      username: u?.username,
      avatar: getProfileAvatarUrl(p.avatar, SUPABASE_URL) || null,
      bio: p.bio || "",
      memo: myMemosMap[p.user_id] || null,
      is_following: myFollowingSet.has(f.follower_id)
    };
  }).filter(Boolean);

  return { results };
}

export async function getCommunityUserFollowingFromSupabase(userId: number) {
  if (!supabase) return null;
  const myUserId = await getMyDjangoUserId();

  const { data: profile } = await supabase.from("common_profile").select("id").eq("user_id", userId).single();
  if (!profile) return { results: [] };

  const { data: follows, error } = await supabase
    .from("common_follow")
    .select("following_id")
    .eq("follower_id", profile.id)
    .order("created", { ascending: false });

  if (error || !follows) return { results: [] };

  const followingObjIds = follows.map(f => f.following_id);
  if (followingObjIds.length === 0) return { results: [] };

  const { data: profiles } = await supabase.from("common_profile").select("id, user_id, nickname, bio, avatar").in("id", followingObjIds);
  const profileMap = (profiles || []).reduce((acc: any, p: any) => { acc[p.id] = p; return acc; }, {});
  
  const userIds = profiles?.map(p => p.user_id) || [];
  const { data: users } = await supabase.from("auth_user").select("id, username").in("id", userIds);
  const userMap = (users || []).reduce((acc: any, u: any) => { acc[u.id] = u; return acc; }, {});

  // My follows
  let myFollowingSet = new Set<number>();
  let myMemosMap: any = {};
  if (myUserId) {
    const { data: myProfile } = await supabase.from("common_profile").select("id").eq("user_id", myUserId).single();
    if (myProfile) {
      const { data: myFollows } = await supabase.from("common_follow").select("following_id").eq("follower_id", myProfile.id).in("following_id", followingObjIds);
      (myFollows || []).forEach(f => myFollowingSet.add(f.following_id));
    }
    const { data: myMemos } = await supabase.from("common_memo").select("target_id, content, color").eq("author_id", myUserId).in("target_id", userIds);
    (myMemos || []).forEach(m => { myMemosMap[m.target_id] = { content: m.content, color: m.color }; });
  }

  const results = follows.map(f => {
    const p = profileMap[f.following_id];
    if (!p) return null;
    const u = userMap[p.user_id];
    return {
      id: p.user_id,
      nickname: p.nickname || u?.username,
      username: u?.username,
      avatar: getProfileAvatarUrl(p.avatar, SUPABASE_URL) || null,
      bio: p.bio || "",
      memo: myMemosMap[p.user_id] || null,
      is_following: myFollowingSet.has(f.following_id)
    };
  }).filter(Boolean);

  return { results };
}
